import { TIMEFRAME_LABELS } from 'constants/chart'
import { useTransformedChartData } from 'hooks/chart'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { IconType } from 'react-icons'
import {
  FiBarChart2 as VolumeIcon,
  FiDollarSign as FeesIcon,
  FiLayers as TvlIcon,
  FiPercent as AprIcon,
  FiTrendingUp as PriceIcon,
} from 'react-icons/fi'
import { FiDroplet as LiquidityIcon } from 'react-icons/fi'
import { AnalyticsView, BaseChartData, ChartDayData, PriceChartEntry, TimeFrame } from 'types/analytics'
import { AprChartPoint, buildAprChartSeries } from 'utils/analytics'
import { cn } from 'utils/analytics/cn'
import { formatUTCDay } from 'utils/analytics/date'
import { formatPercentage } from 'utils/numbers'

import BarChart from '../Chart/BarChart'
import LineChart from '../Chart/LineChart'
import PoolLiquidityChart from '../Chart/PoolLiquidityChart'

type ChartViewKey = 'volume' | 'fees' | 'tvl' | 'price' | 'apr' | 'liquidity'

/**
 * Chart inputs expect latest metrics first and price history oldest first.
 * - `data` should be sorted descending so index 0 is the newest day.
 * - `priceData` should be sorted ascending so the most recent entry is last.
 */
type DetailsChartsViewProps = {
  view: AnalyticsView
  poolId?: string
  data?: ChartDayData[]
  priceData?: PriceChartEntry[]
  hideTVLChart?: boolean
  initialChartView?: ChartViewKey
  liquidityChartConfig?: {
    token0: {
      address?: string
      symbol?: string
      name?: string
      decimals?: number
    }
    token1: {
      address?: string
      symbol?: string
      name?: string
      decimals?: number
    }
    feeTier?: number | null
    currentPrice?: number
    enabled?: boolean
  }
}

const getInitialChartView = (view: AnalyticsView, hideTVLChart?: boolean): ChartViewKey => {
  if (view === 'tokens') return 'price'
  if (view === 'pools' && !hideTVLChart) return 'tvl'
  return 'volume'
}

export default function DetailsChartsView({
  view,
  poolId,
  data,
  priceData,
  hideTVLChart,
  initialChartView,
  liquidityChartConfig,
}: DetailsChartsViewProps) {
  const [selectedChartView, setSelectedChartView] = useState<ChartViewKey>(
    () => initialChartView ?? getInitialChartView(view, hideTVLChart)
  )
  const [tvlTimeframe, setTvlTimeframe] = useState<TimeFrame>('D')
  const [volumeTimeframe, setVolumeTimeframe] = useState<TimeFrame>('D')
  const [feesTimeframe, setFeesTimeframe] = useState<TimeFrame>('D')
  const [aprTimeframe, setAprTimeframe] = useState<TimeFrame>('D')

  const latestEntry = data?.[0]

  useEffect(() => {
    setVolumeTimeframe('D')
    setFeesTimeframe('D')
    setAprTimeframe('D')
    setTvlTimeframe('D')
  }, [view, poolId])

  useEffect(() => {
    setSelectedChartView((current) => {
      const hasLiquidityChart = Boolean(liquidityChartConfig)
      const fallback = (initialChartView ?? getInitialChartView(view, hideTVLChart)) as ChartViewKey
      if (view === 'tokens') {
        return fallback
      }
      const allowedKeys: ChartViewKey[] =
        view === 'pools'
          ? hideTVLChart
            ? hasLiquidityChart
              ? ['volume', 'liquidity', 'fees', 'apr']
              : ['volume', 'fees', 'apr']
            : hasLiquidityChart
            ? ['tvl', 'volume', 'liquidity', 'fees', 'apr']
            : ['tvl', 'volume', 'fees', 'apr']
          : ['price', 'tvl', 'volume']
      if (
        !current ||
        !allowedKeys.includes(current) ||
        (current === 'tvl' && hideTVLChart) ||
        (current === 'liquidity' && !hasLiquidityChart)
      ) {
        return fallback
      }
      return current ?? fallback
    })
  }, [view, poolId, hideTVLChart, initialChartView, liquidityChartConfig])

  const chronologicalData = useMemo(() => {
    if (!data) return []
    return [...data].reverse()
  }, [data])

  const {
    formattedTVLData,
    formattedVolumeData,
    formattedFeesData,
  }: {
    formattedTVLData: BaseChartData[]
    formattedVolumeData: BaseChartData[]
    formattedFeesData: BaseChartData[]
  } = useMemo(() => {
    if (chronologicalData.length === 0) {
      return {
        formattedTVLData: [],
        formattedVolumeData: [],
        formattedFeesData: [],
      }
    }

    const tvl: BaseChartData[] = []
    const volume: BaseChartData[] = []
    const fees: BaseChartData[] = []

    chronologicalData.forEach((entry) => {
      const time = formatUTCDay(entry.date)
      tvl.push({ time, value: entry.tvlUSD ?? 0 })
      volume.push({ time, value: entry.volumeUSD ?? 0 })
      fees.push({ time, value: entry.feesUSD ?? 0 })
    })

    return {
      formattedTVLData: tvl,
      formattedVolumeData: volume,
      formattedFeesData: fees,
    }
  }, [chronologicalData])

  const weeklyVolumeData = useTransformedChartData(chronologicalData, 'week', 'volumeUSD')
  const monthlyVolumeData = useTransformedChartData(chronologicalData, 'month', 'volumeUSD')
  const weeklyFeesData = useTransformedChartData(chronologicalData, 'week', 'feesUSD')
  const monthlyFeesData = useTransformedChartData(chronologicalData, 'month', 'feesUSD')
  const aprSeries = useMemo<Record<TimeFrame, AprChartPoint[]>>(() => {
    const emptySeries: Record<TimeFrame, AprChartPoint[]> = { D: [], W: [], M: [] }
    if (view !== 'pools') return emptySeries
    return buildAprChartSeries(chronologicalData)
  }, [chronologicalData, view])

  const selectedAprData = useMemo<AprChartPoint[]>(() => aprSeries[aprTimeframe], [aprSeries, aprTimeframe])
  const latestAprPoint = selectedAprData.length ? selectedAprData[selectedAprData.length - 1] : undefined

  const tvlDefaultValue = latestEntry ? Number(latestEntry.tvlUSD ?? 0) : undefined
  const volumeDefaultValue = latestEntry ? Number(latestEntry.volumeUSD ?? 0) : undefined
  const feesDefaultValue = latestEntry ? Number(latestEntry.feesUSD ?? 0) : undefined
  const latestPricePoint = priceData && priceData.length ? priceData[priceData.length - 1] : undefined
  const priceDefaultValue = latestPricePoint?.value

  const aprYAxisFormatter = useCallback((nextValue: number) => {
    const formatted = formatPercentage(nextValue.toString())
    if (formatted === '-') return formatted
    return formatted.replace(/(\.\d*?[1-9])0+%$/, '$1%').replace(/\.0+%$/, '%')
  }, [])

  const tvlSummaryProps = useMemo(
    () => ({
      title: 'TVL',
    }),
    []
  )

  const volumeSummaryProps = useMemo(
    () => ({
      title: 'Volume',
      timeframeLabel: TIMEFRAME_LABELS[volumeTimeframe],
    }),
    [volumeTimeframe]
  )

  const feesSummaryProps = useMemo(
    () => ({
      title: 'Fees',
      timeframeLabel: TIMEFRAME_LABELS[feesTimeframe],
    }),
    [feesTimeframe]
  )

  const formatAprValue = useCallback(
    (value?: number) =>
      typeof value === 'number' && Number.isFinite(value) ? formatPercentage(value.toString()) : '-',
    []
  )

  const aprSummaryProps = useMemo(
    () => ({
      title: 'Historical APR',
      valueFormatter: formatAprValue,
    }),
    [formatAprValue]
  )

  const priceSummaryProps = useMemo(
    () => ({
      title: 'Price',
      isPrice: true,
    }),
    []
  )

  const ChartViewToggle = useMemo(() => {
    const options: { key: ChartViewKey; label: string; Icon: IconType }[] = []

    if (view === 'tokens') {
      options.push({ key: 'price', label: 'Price', Icon: PriceIcon })
      options.push({ key: 'tvl', label: 'TVL', Icon: TvlIcon })
      options.push({ key: 'volume', label: 'Volume', Icon: VolumeIcon })
    } else {
      if (!hideTVLChart) {
        options.push({ key: 'tvl', label: 'TVL', Icon: TvlIcon })
      }
      options.push({ key: 'volume', label: 'Volume', Icon: VolumeIcon })
      if (liquidityChartConfig) {
        options.push({ key: 'liquidity', label: 'Liquidity', Icon: LiquidityIcon })
      }
      options.push({ key: 'fees', label: 'Fees', Icon: FeesIcon })
      options.push({ key: 'apr', label: 'APR', Icon: AprIcon })
    }

    return (
      <div
        className="flex gap-1 rounded-md bg-kodiak-card-tertiary px-1 py-1 overflow-x-auto"
        role="group"
        aria-label="Chart view selector"
      >
        {options.map(({ key, label, Icon }) => {
          const isSelected = selectedChartView === key
          return (
            <button
              key={key}
              type="button"
              className={cn(
                'cursor-pointer px-3 py-1.5 rounded-sm transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kodiak-brand-primary inline-flex items-center gap-2 flex-1 justify-center',
                isSelected
                  ? 'bg-kodiak-bg text-kodiak-brand-primary'
                  : 'bg-transparent text-kodiak-text-quaternary hover:text-kodiak-text-primary'
              )}
              aria-pressed={isSelected}
              onClick={() => setSelectedChartView(key as ChartViewKey)}
            >
              <Icon size={14} className="shrink-0" />
              <span className="text-sm font-medium">{label}</span>
            </button>
          )
        })}
      </div>
    )
  }, [selectedChartView, view, hideTVLChart, liquidityChartConfig])

  let chartContent: JSX.Element

  if (selectedChartView === 'liquidity' && view === 'pools' && liquidityChartConfig) {
    chartContent = (
      <PoolLiquidityChart
        token0={liquidityChartConfig.token0}
        token1={liquidityChartConfig.token1}
        feeTier={liquidityChartConfig.feeTier}
        currentPrice={liquidityChartConfig.currentPrice}
        enabled={liquidityChartConfig.enabled}
      />
    )
  } else if (selectedChartView === 'tvl' && !hideTVLChart) {
    chartContent = (
      <LineChart
        data={formattedTVLData}
        className="min-w-0 min-h-[270px] lg:min-h-[428px]"
        minHeight={308}
        showTimeSelector
        selectedTimeframe={tvlTimeframe}
        onTimeframeChange={setTvlTimeframe}
        summaryProps={tvlSummaryProps}
        defaultValue={tvlDefaultValue}
      />
    )
  } else if (selectedChartView === 'fees' && view === 'pools') {
    chartContent = (
      <BarChart
        data={feesTimeframe === 'M' ? monthlyFeesData : feesTimeframe === 'W' ? weeklyFeesData : formattedFeesData}
        summaryProps={feesSummaryProps}
        defaultValue={feesDefaultValue}
        selectedTimeframe={feesTimeframe}
        onTimeframeChange={setFeesTimeframe}
        showTimeSelector
      />
    )
  } else if (selectedChartView === 'apr' && view === 'pools') {
    chartContent = (
      <BarChart
        data={selectedAprData}
        className="min-w-0 min-h-[270px] lg:min-h-[428px]"
        minHeight={308}
        summaryProps={aprSummaryProps}
        defaultValue={latestAprPoint?.value}
        selectedTimeframe={aprTimeframe}
        onTimeframeChange={setAprTimeframe}
        showTimeSelector
        yAxisTickFormatter={aprYAxisFormatter}
      />
    )
  } else if (selectedChartView === 'price' && view === 'tokens') {
    chartContent = (
      <LineChart
        data={priceData ?? []}
        className="min-w-0 min-h-[270px] lg:min-h-[428px]"
        minHeight={310}
        summaryProps={priceSummaryProps}
        defaultValue={priceDefaultValue}
      />
    )
  } else {
    chartContent = (
      <BarChart
        data={
          volumeTimeframe === 'M' ? monthlyVolumeData : volumeTimeframe === 'W' ? weeklyVolumeData : formattedVolumeData
        }
        summaryProps={volumeSummaryProps}
        defaultValue={volumeDefaultValue}
        selectedTimeframe={volumeTimeframe}
        onTimeframeChange={setVolumeTimeframe}
        showTimeSelector
      />
    )
  }

  return (
    <div className="flex flex-col gap-3 lg:gap-4 w-full min-h-[428px] h-full">
      <div className="w-full">{ChartViewToggle}</div>
      {chartContent}
    </div>
  )
}
